import { EmeraldError } from 'src/common/api/emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { BRMInputActions } from 'src/common/redux/reducers/brm-input-reducer';
import { ILineItemAddData, lineItemAdd } from 'src/items-list/business-logic/line-item-add';
import { CharacterCodes } from '../../common/enumerations/character-codes';
import { ScannerCodes } from '../../common/enumerations/scanner-codes';
import { removeSpecialCharacters } from '../../common/helpers/character-escaper';
import { getValueAsString } from '../../common/helpers/string-helper';
import { IItemLookup } from '../../common/interfaces/dtos/IItemLookup';
import { IItemInputData } from '../../common/interfaces/IItemInputData';
import { store } from '../../common/redux/configure-store';
import { addLookupLineItem } from '../../items-list/business-logic/add-lookup-line-item';
import { addTransactionLine } from '../../items-list/business-logic/add-transaction-line';
import { initiateTransaction } from '../../items-list/business-logic/initiate-transaction';
import { addFirstTransactionLine } from './add-first-transaction-line';
import { voidEmptyTransaction } from './void-transaction';

export const addItemToTransaction = async (inputData: IItemInputData): Promise<void> => {
  if (isValidCharacters(inputData)) {
    if (store.getState().transaction.transactionIdExists) {
      await addTransactionLine(inputData);
    } else {
      await addFirstTransactionLine(inputData);
    }
  } else {
    throw new EmeraldError('Invalid_Format', 'Please enter valid itemId');
  }
};

export const addLookedupItemToTransaction = async (
  inputData: IItemInputData,
  itemLookup: IItemLookup,
  isWeightEmbeddedInBarcode?: boolean,
): Promise<void> => {
  const hasTransaction = store.getState().transaction.transactionIdExists;
  const itemId = inputData.itemId || itemLookup.itemId;

  if (itemLookup.hasPrice || itemLookup.requiredPrice) {
    if (!hasTransaction) {
      await initiateTransaction();
    }

    return addLookupLineItem({ inputData, itemId, itemLookup, isWeightEmbeddedInBarcode }).catch(
      err => {
        if (!hasTransaction) {
          voidEmptyTransaction();
        }
        throw err;
      },
    );
  } else {
    throw new EmeraldError('NoItemPriceError');
  }
};

export const addLineItemToTransaction = async (lineItem: ILineItemAddData): Promise<void> => {
  const hasTransaction = store.getState().transaction.transactionIdExists;

  return withSpinner(async () => {
    if (!hasTransaction) {
      await initiateTransaction();
    }
    return lineItemAdd(lineItem)
      .catch(err => {
        if (!hasTransaction) {
          voidEmptyTransaction();
        }
        throw err;
      })
      .finally(() => {
        store.dispatch(BRMInputActions.resetState());
      });
  });
};

const isValidCharacters = (data: IItemInputData): boolean => {
  if (is2DBarcode(data.scanDataType)) {
    return true;
  }
  const escapedInput = removeSpecialCharacters(getValueAsString(data.itemId ?? ''));
  const len = escapedInput.length;
  for (let i = 0; i < len; i++) {
    if (!isLetterOrDigit(escapedInput.charCodeAt(i))) {
      return false;
    }
  }
  return true;
};

export const isLetterOrDigit = (char: number): boolean => {
  return (
    (char >= CharacterCodes.a && char <= CharacterCodes.z) ||
    (char >= CharacterCodes.A && char <= CharacterCodes.Z) ||
    (char >= CharacterCodes._0 && char <= CharacterCodes._9)
  );
};

const is2DBarcode = (scanDataType?: number): boolean => {
  if (scanDataType) {
    if (
      scanDataType === ScannerCodes.SCAN_SDT_UPDF417 ||
      scanDataType === ScannerCodes.SCAN_SDT_PDF417
    ) {
      return true;
    }
  }
  return false;
};
